import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatTreeModule } from "@angular/material/tree";
import { select } from "@ngxs/store";
import { AccessControlService, AuthState } from "@dis/auth";
import { MatIconModule } from "@angular/material/icon";
import { MatListModule } from "@angular/material/list";
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from "@angular/router";
import { CommonModule } from "@angular/common";
import { filter, map } from "rxjs";
import { navGroups, type NavEntry, type NavGroupEntries } from "../../../core/routing/nav";
import type { NavGroup } from "../../../core/routing/route-data";

/** Route node data with possible child nodes. */
export interface RouteTreeNode {
  name: string;
  icon?: string;
  route?: string[];
  children?: RouteTreeNode[];
  isLeafNode?: boolean;
  /** A non-link label splitting a merged menu's groups ("Projects", "Sales"). */
  isSectionTitle?: boolean;
  /** The groups' URL prefixes ("/operations"); the menu shows as active anywhere under them. */
  activePrefixes?: string[];
  /** Active only on an exact URL match, so a group's Overview isn't active on every page in the group. */
  exactActive?: boolean;
}

const HOME: RouteTreeNode = { name: "Home", icon: "home", route: ["/home"] };
const USER: RouteTreeNode = { name: "User", icon: "account_circle", route: ["/auth", "signin"] };

// Groups shown as one menu, each under its own section title. Only the nav changes: routes and URLs stay per group.
// None for now; e.g. { name: "Sales/Planning", icon: "storefront", groups: ["projects", "sales"] }.
const MERGED_MENUS: { name: string; icon: string; groups: NavGroup[] }[] = [];

const leaf = (entry: NavEntry): RouteTreeNode => ({
  name: entry.title,
  route: entry.link,
  isLeafNode: true,
  exactActive: entry.link.length === 2,
});

const groupNode = ({ group, label, icon, entries }: NavGroupEntries): RouteTreeNode =>
  entries.length === 1
    ? { name: label, icon, route: entries[0].link }
    : { name: label, icon, activePrefixes: [`/${group}`], children: entries.map(leaf) };

// A merged menu takes the place of its first visible group; with only one of its groups visible it's that group alone.
function menuNodes(groups: NavGroupEntries[]): RouteTreeNode[] {
  const nodes: RouteTreeNode[] = [];
  for (const group of groups) {
    const menu = MERGED_MENUS.find((candidate) => candidate.groups.includes(group.group));
    const members = menu ? groups.filter((candidate) => menu.groups.includes(candidate.group)) : [];
    if (!menu || members.length < 2) {
      nodes.push(groupNode(group));
    } else if (members[0] === group) {
      nodes.push({
        name: menu.name,
        icon: menu.icon,
        activePrefixes: members.map((member) => `/${member.group}`),
        children: members.flatMap((member) => [
          { name: member.label, isLeafNode: true, isSectionTitle: true },
          ...member.entries.map(leaf),
        ]),
      });
    }
  }
  return nodes;
}

@Component({
  selector: "app-nav-tree",
  templateUrl: "./nav-tree.component.html",
  styleUrls: ["./nav-tree.component.css", "./animations/tree-node-animation.scss"],
  imports: [MatIconModule, MatListModule, MatTreeModule, RouterLinkActive, RouterLink, CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavTreeComponent {
  #accessControl = inject(AccessControlService);
  #router = inject(Router);

  // Built from the group route files' navOrder/title/scopes (core/routing/nav.ts). Headers only expand; each
  // group's first entry is its Overview. A group with a single page (Map) is a plain top-level link.
  dataSource = computed<RouteTreeNode[]>(() => [HOME, ...menuNodes(navGroups(this.#accessControl)), USER]);
  $claims = select(AuthState.claims);

  #url = toSignal(
    this.#router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.#router.url)
    ),
    { initialValue: this.#router.url }
  );

  expandedNode: RouteTreeNode | null = null;

  childrenAccessor = (node: RouteTreeNode) => node.children ?? [];

  /** Get whether the node has children or not. */
  hasChild = (_: number, node: RouteTreeNode) => !!node.children && node.children.length > 0 && !node.isLeafNode;
  isLeafNode = (_: number, node: RouteTreeNode) => !!node.isLeafNode && !node.isSectionTitle;
  isSectionTitle = (_: number, node: RouteTreeNode) => !!node.isSectionTitle;

  toggle(node: RouteTreeNode) {
    this.expandedNode = this.expandedNode === node ? null : node;
  }

  isParentActive(parentNode: RouteTreeNode): boolean {
    const path = this.#url().split(/[?#(]/)[0];
    return (parentNode.activePrefixes ?? []).some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
  }
}
